import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { initializeAuthStart, initializeAuthSuccess, initializeAuthFailure } from './store/slices/authSlice';
import apiClient from './services/api';
import { getAccountStatus } from './services/api';
import { Toaster } from 'react-hot-toast';
import ConfirmHost from './components/ConfirmHost';
import './App.css';

// Initialize i18n
import './i18n/config';

// Components
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import SignUp from './components/SignUp';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AgentConfig from './components/AgentConfig';
import Posts from './components/Posts';
// PostValidation removed — posts are auto-approved after video generation
import Escalations from './components/Escalations';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import ConversationDetail from './components/ConversationDetail';
import WhatsAppNumbers from './components/WhatsAppNumbersOAuth';
import LiveChat from './components/LiveChat';
import ConversationInbox from './components/ConversationInbox';
import Templates from './components/Templates';
import DeviceManagement from './components/DeviceManagement';
import DeletionWarningBanner from './components/DeletionWarningBanner';
import Profile from './components/Profile';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import ToneArchetypeRequiredModal from './components/ToneArchetypeRequiredModal';
import SocialSettings from './components/SocialSettings';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import './components/AnalyticsDashboard.css';
import './components/WhatsAppNumbers.css';

// Admin App
import AdminApp from './admin/AdminApp';

// Helper function to normalize paths and prevent double slashes
const normalizePath = (path) => {
  if (!path || path === '/') return '/';
  // Replace multiple consecutive slashes with single slash, then ensure single leading slash
  const normalized = path.replace(/\/+/g, '/');
  // Ensure it starts with exactly one slash
  return normalized.startsWith('/') ? normalized : '/' + normalized;
};

// Public Route Component - Always shows content with header (header shows login button if not authenticated)
function PublicRoute({ children }) {
  const { initializing } = useSelector((state) => state.auth);
  
  // Show loading while initializing
  if (initializing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  return (
    <>
      <Header />
      {children}
    </>
  );
}

// Protected Route Component - Requires authentication
// Optional requiredFeature prop: if set, user must have that feature_flag (admins bypass)
function ProtectedRoute({ children, requiredFeature }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, user, initializing } = useSelector((state) => state.auth);
  const [accountStatus, setAccountStatus] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const onboardingStatusCheckedForPathRef = useRef(null);
  const isCheckingOnboardingRef = useRef(false);
  const [checkingDeletion, setCheckingDeletion] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [onboardingCheckError, setOnboardingCheckError] = useState(null);
  const [onboardingRetryKey, setOnboardingRetryKey] = useState(0);
  
  // Admins / superadmins never go through user-onboarding (no voice
  // recording, no tone archetype, no contract acceptance). Skipping the
  // onboarding-status pre-check for them avoids:
  //   - the redirect to ``/onboarding`` for an admin who has
  //     ``onboarding_completed=false`` by default,
  //   - the non-dismissible tone-archetype modal popping up on the
  //     dashboard when ``tone_archetype`` is null,
  //   - a re-render storm from the ``[user]`` dep on the effect below
  //     each time the modal/Redux roundtrip flips the user reference.
  const isAdminUser = !!(user && (user.role === 'admin' || user.role === 'superadmin' || user.is_superuser));

  // Check account deletion status and onboarding status when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const checkStatuses = async () => {
        try {
          setCheckingDeletion(true);
          setOnboardingCheckError(null);

          // Check deletion status
          const deletionResponse = await getAccountStatus();
          setAccountStatus(deletionResponse.data);

          // Admins skip the onboarding pre-check entirely.
          if (isAdminUser) {
            onboardingStatusCheckedForPathRef.current = location.pathname;
            isCheckingOnboardingRef.current = false;
            // Use a functional setter that returns the current value
            // when it's already the admin-bypass shape — avoids a
            // re-render with a brand-new object literal on every effect
            // run, which downstream effects (Dashboard) would otherwise
            // see as ``onboardingStatus`` changing.
            setOnboardingStatus((prev) =>
              prev && prev.onboarding_completed === true && prev.__adminBypass
                ? prev
                : { onboarding_completed: true, __adminBypass: true }
            );
            return;
          }

          // Check onboarding status (skip if already on onboarding page)
          if (location.pathname !== '/onboarding') {
            // Only check if we haven't already checked for this path and we're not already checking
            if (onboardingStatusCheckedForPathRef.current !== location.pathname && !isCheckingOnboardingRef.current) {
              try {
                isCheckingOnboardingRef.current = true;
                setCheckingOnboarding(true);
                const onboardingResponse = await apiClient.get('/api/v1/onboarding/status');
                setOnboardingStatus(onboardingResponse.data);
                onboardingStatusCheckedForPathRef.current = location.pathname;
              } catch (err) {
                // Fail closed: if we can't verify onboarding status, don't allow access to protected content.
                console.error('Failed to check onboarding status:', err);
                setOnboardingCheckError('Failed to verify onboarding status. Please retry.');
                setOnboardingStatus(null);
                onboardingStatusCheckedForPathRef.current = null;
              } finally {
                setCheckingOnboarding(false);
                isCheckingOnboardingRef.current = false;
              }
            }
          } else {
            // On onboarding page, clear the check so it can be re-checked when navigating away
            onboardingStatusCheckedForPathRef.current = null;
            setOnboardingStatus(null);
            isCheckingOnboardingRef.current = false;
          }
        } catch (err) {
          console.error('Failed to load account status:', err);
        } finally {
          setCheckingDeletion(false);
        }
      };
      checkStatuses();
    }
    // Depend on PRIMITIVE values, not on the ``user`` object itself.
    // Redux assigns a brand-new ``user`` reference on every dispatch
    // of ``initializeAuthSuccess`` (e.g. the ``/auth/me`` refresh fired
    // by OnboardingWizard or by ToneArchetypeRequiredModal.onComplete),
    // even when the underlying fields are unchanged. Listing ``user``
    // in the dep array would refire this effect on every such dispatch
    // and produce a storm of ``/account-status`` /
    // ``/onboarding/status`` calls — a "loop" in the network tab.
    // ``user?.id`` and the role flags are primitives and only change
    // when the actual user identity / privilege changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, user?.role, user?.is_superuser, location.pathname, onboardingRetryKey]);
  
  // Show loading while initializing or checking statuses
  if (initializing || checkingDeletion || (location.pathname !== '/onboarding' && checkingOnboarding)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Fail closed: if we couldn't verify onboarding status, block protected content behind a retry UI.
  if (location.pathname !== '/onboarding' && onboardingCheckError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24 }}>
        <div style={{ maxWidth: 520 }}>
          <h3 style={{ marginTop: 0 }}>Unable to verify onboarding status</h3>
          <p>{onboardingCheckError}</p>
          <button onClick={() => setOnboardingRetryKey((k) => k + 1)} style={{ padding: '10px 14px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated (no user data means not authenticated)
  if (!isAuthenticated || !user) {
    // Normalize path to prevent double slashes
    const loginPath = normalizePath('/login');
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  
  // Redirect to dashboard if account is scheduled for deletion
  // User Registration is a public route, so it's still accessible
  if (accountStatus?.has_active_deletion_request) {
    // Normalize path to prevent double slashes
    const dashboardPath = normalizePath('/');
    return <Navigate to={dashboardPath} replace />;
  }
  
  // Redirect to onboarding if not completed (skip if already on
  // onboarding page or the user is an admin/superadmin — staff don't
  // go through the end-user onboarding pipeline).
  // Allow access to /onboarding even if completed (user might want to review).
  // Only redirect if we've finished checking and have a confirmed status
  if (
    location.pathname !== '/onboarding' &&
    !isAdminUser &&
    !checkingOnboarding &&
    onboardingStatusCheckedForPathRef.current === location.pathname &&
    onboardingStatus &&
    !onboardingStatus.onboarding_completed
  ) {
    // Normalize path to prevent double slashes
    const onboardingPath = normalizePath('/onboarding');
    return <Navigate to={onboardingPath} replace />;
  }
  
  // Don't redirect away from /onboarding - let OnboardingWizard handle its own logic

  // Feature-flag gating: redirect to dashboard if user lacks the required feature
  if (requiredFeature && user) {
    const isAdmin = user.role === 'admin' || user.role === 'superadmin' || user.is_superuser;
    const userFlags = user.feature_flags || {};
    if (!isAdmin && !userFlags[requiredFeature]) {
      const dashboardPath = normalizePath('/dashboard');
      return <Navigate to={dashboardPath} replace />;
    }
  }

  // Show non-dismissible tone archetype picker for onboarded users who
  // haven't selected yet.
  //
  // Bypassed for:
  //   - the onboarding page itself (handled by OnboardingWizard),
  //   - admin and superadmin users (the tone archetype drives AI
  //     content generation for end users, not staff — surfacing the
  //     blocking modal to admins traps them on the dashboard with no
  //     way out),
  //   - users that already have a ``tone_archetype`` set in Redux (the
  //     PUT and the ``/auth/me`` refresh can land in different ticks;
  //     guarding on the freshest Redux value makes the dismissal
  //     idempotent and prevents flicker on the next render).
  const showToneModal =
    location.pathname !== '/onboarding' &&
    user &&
    !isAdminUser &&
    !user.tone_archetype &&
    onboardingStatus?.onboarding_completed === true;

  return (
    <>
      <Header />
      {showToneModal && (
        <ToneArchetypeRequiredModal
          onComplete={() => {
            // Reload user data into Redux so the modal unmounts. Only
            // dispatch if ``tone_archetype`` actually changed — avoids
            // an unnecessary user-reference flip that would cascade
            // into every ``[user]``-keyed effect in the app.
            apiClient.get('/api/v1/auth/me').then((res) => {
              const fresh = res?.data;
              if (!fresh) return;
              if (fresh.tone_archetype !== user?.tone_archetype) {
                dispatch(initializeAuthSuccess({ user: fresh }));
              }
            }).catch(() => {
              window.location.reload();
            });
          }}
        />
      )}
      {children}
    </>
  );
}


function App() {
  const dispatch = useDispatch();
  const { user, initializing } = useSelector((state) => state.auth);

  // Initialize authentication on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      // If we have a token, try to fetch user data
      if (storedToken) {
        // initializing is already true from initial state
        try {
          // Try to fetch user data with the stored token
          const response = await apiClient.get('/api/v1/auth/me');
          if (response.data) {
            dispatch(initializeAuthSuccess({ user: response.data }));
          } else {
            // No user data in response
            dispatch(initializeAuthFailure());
          }
        } catch (error) {
          // Token is invalid or expired, clear it
          console.error('Auth initialization failed:', error.response?.status, error.message);
          dispatch(initializeAuthFailure());
        }
      }
      // If no token, initialization is already complete (initializing starts as false)
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Show loading state while initializing
  if (initializing) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { fontSize: '14px', maxWidth: '440px' },
          success: { iconTheme: { primary: '#16a34a', secondary: '#fff' } },
          error: { iconTheme: { primary: '#dc2626', secondary: '#fff' } },
        }}
      />
      <ConfirmHost />
      {user && <DeletionWarningBanner />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<SignUp />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <Dashboard />
            </PublicRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <PublicRoute>
              <Dashboard />
            </PublicRoute>
          } 
        />
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/analytics/conversation/:conversationId" 
          element={
            <ProtectedRoute>
              <ConversationDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/live-chat/:conversationId" 
          element={
            <ProtectedRoute>
              <LiveChat />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/conversations" 
          element={
            <ProtectedRoute>
              <ConversationInbox />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/whatsapp" 
          element={
            <ProtectedRoute>
              <WhatsAppNumbers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/templates" 
          element={
            <ProtectedRoute>
              <Templates />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/agents" 
          element={
            <ProtectedRoute>
              <AgentConfig />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/posts" 
          element={
            <ProtectedRoute>
              <Posts />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/escalations" 
          element={
            <ProtectedRoute>
              <Escalations />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/account/security" 
          element={
            <ProtectedRoute>
              <DeviceManagement />
            </ProtectedRoute>
          } 
        />
        {/* Alias for backward compatibility with email links */}
        <Route 
          path="/account-security" 
          element={
            <ProtectedRoute>
              <DeviceManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/account/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/onboarding" 
          element={
            <ProtectedRoute>
              <OnboardingWizard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings/social" 
          element={
            <ProtectedRoute requiredFeature="can_connect_socials">
              <SocialSettings />
            </ProtectedRoute>
          } 
        />
        {/* Admin Routes */}
        <Route path="/admin/*" element={<AdminApp />} />
      </Routes>
    </div>
  );
}

export default App;
